import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const rupees = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const DealerStatementDialog = ({ partyId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!partyId) return;
    setLoading(true);
    authFetch(`${API}/parties/${partyId}/statement?limit=200`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setData(d.data); })
      .finally(() => setLoading(false));
  }, [partyId]);

  const exportCSV = () => {
    if (!data?.entries?.length) return;
    const header = "Date,Type,ID,Description,Debit,Credit,RunningBalance\n";
    const rows = data.entries.map((e) =>
      [e.date, e.type, e.id, `"${(e.description || "").replace(/"/g, '""')}"`,
        (e.debit || 0).toFixed(2), (e.credit || 0).toFixed(2), (e.running_balance || 0).toFixed(2)].join(",")
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `statement_${partyId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#8B1A1A]" />
            Dealer Statement — {data?.party?.firm_name || partyId}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><div className="w-6 h-6 spinner mx-auto" /></div>
        ) : !data ? (
          <p className="text-sm text-slate-500 text-center py-8">No data available</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-2">
                <p className="text-[10px] uppercase text-slate-500">Credit Limit</p>
                <p className="font-semibold text-sm">{rupees(data.party?.credit_limit)}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-2">
                <p className="text-[10px] uppercase text-slate-500">Total Billed</p>
                <p className="font-semibold text-sm">{rupees(data.total_orders_value)}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-2">
                <p className="text-[10px] uppercase text-slate-500">Total Collected</p>
                <p className="font-semibold text-sm text-emerald-700">{rupees(data.total_collected)}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-2">
                <p className="text-[10px] uppercase text-slate-500">Closing Balance</p>
                <p className={`font-semibold text-sm ${data.closing_balance > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="closing-balance">
                  {rupees(data.closing_balance)}
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-statement-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
              </Button>
            </div>

            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                  <tr>
                    <th className="px-2 py-2 text-left">Date</th>
                    <th className="px-2 py-2 text-left">Type</th>
                    <th className="px-2 py-2 text-left">Description</th>
                    <th className="px-2 py-2 text-right">Debit</th>
                    <th className="px-2 py-2 text-right">Credit</th>
                    <th className="px-2 py-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e, i) => (
                    <tr key={`${e.type}-${e.id}-${i}`} className="border-t" data-testid={`statement-row-${i}`}>
                      <td className="px-2 py-1.5">{e.date}</td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className="text-[10px]">{e.type}</Badge>
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">{e.description}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{e.debit > 0 ? rupees(e.debit) : "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{e.credit > 0 ? rupees(e.credit) : "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-semibold">{rupees(e.running_balance)}</td>
                    </tr>
                  ))}
                  {data.entries.length === 0 && (
                    <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">No transactions yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DealerStatementDialog;
